import React, { useEffect, useState } from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../../utils/imageUrl";
import { IoArrowForward } from "react-icons/io5";
import { setJobsSteps } from "../../store/slices/ui_control_slice";
import { steps_constants, service_booking_keys } from "../../store/constants";

const FourthSection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { get_all_services_data } = useSelector((state) => state.serviceManage);
  const [city, setCity] = useState("");

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
          );
          const data = await res.json();
          const result = data.results?.[0];
          const cityComponent = result?.address_components?.find((c) =>
            c.types.includes("locality")
          );
          if (cityComponent) setCity(cityComponent.long_name);
        } catch {
          setCity("your area");
        }
      },
      () => setCity("your area")
    );
  }, []);

  // Seasonal service names
  const seasonalServices = {
    winter: ["Snow Removal", "Furnace Repair", "HVAC Maintenance", "Gutter Cleaning"],
    spring: ["Lawn Maintenance", "Landscaping", "Power Washing", "Painting"],
    summer: ["Air Conditioning", "Pool Maintenance", "Deck and Fence", "BBQ Cleaning"],
    fall: ["Gutter Cleaning", "Furnace Repair", "Roofing", "Window Cleaning"],
  };

  const getSeason = () => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return "spring";
    if (month >= 5 && month <= 7) return "summer";
    if (month >= 8 && month <= 10) return "fall";
    return "winter";
  };

  const allServices = get_all_services_data?.services || [];
  const keywords = seasonalServices[getSeason()];
  const matched = keywords
    .map((kw) => allServices.find((s) => s.name.toLowerCase().includes(kw.toLowerCase())))
    .filter(Boolean);
  const remaining = allServices.filter((s) => !matched.includes(s));
  const services = [...matched, ...remaining].slice(0, 4);

  if (services.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: "#fff", pt: { xs: 6, md: 8 }, pb: { xs: 5, md: 10 }, px: { xs: 2, sm: 0 } }}>
      <Container maxWidth="lg">
        <Stack alignItems="center" mb={{ xs: 2.5, md: 4 }}>
          <Typography
            sx={{
              fontSize: { xs: "22px", sm: "32px", md: "38px" },
              fontWeight: "700",
              color: "#1a1a1a",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            What homeowners{city ? ` in ${city}` : ""} are booking now
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          {services.map((service, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Stack
                onClick={() => {
                  navigate(`/service-details/${service.slug}`);
                  dispatch(setJobsSteps({ value: null }));
                  localStorage.removeItem(steps_constants.JOBS_STEP);
                  localStorage.removeItem(service_booking_keys.DATE);
                  localStorage.removeItem(service_booking_keys.TIME);
                  localStorage.removeItem(service_booking_keys.DESCRIPTION);
                  localStorage.removeItem(service_booking_keys.LOCATION);
                  localStorage.removeItem(service_booking_keys.PRICE);
                  localStorage.removeItem(service_booking_keys.IMAGES);
                }}
                sx={{
                  cursor: "pointer",
                  borderRadius: "20px",
                  overflow: "hidden",
                  position: "relative",
                  height: { xs: "180px", sm: "200px" },
                  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.02)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
                  },
                  "&:hover .service-img": {
                    transform: "scale(1.1)",
                  },
                  "&:hover .service-overlay": {
                    background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
                  },
                  "&:hover .service-arrow": {
                    opacity: 1,
                    transform: "translateX(0)",
                  },
                }}
              >
                <Box
                  className="service-img"
                  component="img"
                  src={getImageUrl(service.mobile_image || service.image)}
                  alt={service.name}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                <Box
                  className="service-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.05) 45%, transparent 100%)",
                    transition: "background 0.35s ease",
                  }}
                />
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent="space-between"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: { xs: "15px", sm: "16px" },
                      fontWeight: "700",
                      lineHeight: 1.2,
                      textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {service.name}
                  </Typography>
                  <Box
                    className="service-arrow"
                    sx={{
                      opacity: 0,
                      transform: "translateX(-8px)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      ml: 1,
                    }}
                  >
                    <IoArrowForward size={18} color="#fff" />
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Stack alignItems="center" mt={4}>
          <Box
            onClick={() => navigate("/services")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              border: "2px solid #1a1a1a",
              color: "#1a1a1a",
              px: 4,
              py: 1.5,
              borderRadius: "50px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "14px",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#1a1a1a",
                color: "#fff",
              },
            }}
          >
            View all services
            <IoArrowForward size={16} />
          </Box>
        </Stack>

      </Container>
    </Box>
  );
};

export default FourthSection;
